import { IngestionSummary, ProviderSummary } from '../domain/types';
import { countOutcomes, EMPTY_OUTCOME_COUNTS } from './outcome-class.utils';

/** How many newest ingestions the adapter samples per provider. */
export const PROVIDER_SAMPLE_SIZE = 10;

/**
 * Newest first, tie-broken on `eventId` descending the way PDGS orders its
 * own lists — two files can share a `createdAt` second, and "latest" has to
 * mean the same thing here as it does upstream.
 */
const byNewestFirst = (a: IngestionSummary, b: IngestionSummary): number =>
  b.createdAt.localeCompare(a.createdAt) || b.eventId.localeCompare(a.eventId);

const sumRows = (ingestions: IngestionSummary[]): string =>
  ingestions
    .reduce(
      (total, ingestion) => total + BigInt(ingestion.totalRowsParsed),
      BigInt(0),
    )
    .toString();

interface SummarizeInput {
  dataProvider: string;
  hasConfiguration: boolean;
  ingestions: IngestionSummary[];
  /** Newest files to keep. The adapter uses 10; provider detail uses 100. */
  sampleSize?: number;
}

/**
 * Reduces one provider's newest ingestions into a `ProviderSummary`.
 * PDGS returns lists newest-first already; sorting here is defensive so the
 * "latest" is never a matter of what order the wire happened to use.
 */
export const summarizeProviderIngestions = ({
  dataProvider,
  hasConfiguration,
  ingestions,
  sampleSize = PROVIDER_SAMPLE_SIZE,
}: SummarizeInput): ProviderSummary => {
  const sample = [...ingestions].sort(byNewestFirst).slice(0, sampleSize);
  const latest = sample[0];
  const statuses = sample.map(ingestion => ingestion.status);

  return {
    dataProvider,
    hasConfiguration,
    latestIngestion: latest
      ? {
          eventId: latest.eventId,
          fileName: latest.fileName,
          status: latest.status,
          createdAt: latest.createdAt,
        }
      : null,
    recentOutcomes: statuses,
    recentCounts: countOutcomes(statuses),
    recentRowsParsed: sumRows(sample),
    window: { kind: 'lastFiles', size: sampleSize },
    loadError: false,
  };
};

/** Placeholder for a provider whose probe failed. */
export const buildUnavailableProviderSummary = (
  dataProvider: string,
  hasConfiguration: boolean,
): ProviderSummary => ({
  dataProvider,
  hasConfiguration,
  latestIngestion: null,
  recentOutcomes: [],
  recentCounts: { ...EMPTY_OUTCOME_COUNTS },
  recentRowsParsed: '0',
  window: { kind: 'lastFiles', size: PROVIDER_SAMPLE_SIZE },
  loadError: true,
});
